import React, { useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  selectEmailResponse,
  selectEmailResponseAiAgentId,
  selectEmailResponseCitationIsLoading,
  selectEmailResponseEditing,
  selectEmailResponseInteractionLoading,
  setEmailResponse,
  setEmailResponseAiAgentId,
  setEmailResponseInteractionLoading,
} from '../../../store/emailHub/emailResponse'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { EmailResponseResult } from '../../../models/EmailResponse'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useInteractWithAiAgentStreamingMutation } from '../../../store/ai/aiAgent'
import { isEmailResponseNoResult } from './utils'
import { StreamingAIAgentChat } from '../../AIAgent/Chat/StreamingAIAgentChat'
import { addMessage, selectLastUserChatMessage, updateStreamingMessage } from '../../../store/streamingChat/streamingChat'
import { useStreamingChat } from '../../AIAgent/hooks/useStreamingChat'

export interface EmailResponseChatProps {
  threadId: string
}

export const EmailResponseChat: React.FC<EmailResponseChatProps> = ({ threadId }) => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)
  const isLoading = useAppSelector(selectEmailResponseInteractionLoading)
  const isEditing = useAppSelector(selectEmailResponseEditing)
  const aiAgentId = useAppSelector(selectEmailResponseAiAgentId)
  const citationIsLoading = useAppSelector(selectEmailResponseCitationIsLoading)
  const emailResponse = useAppSelector(selectEmailResponse)
  const lastUserInteraction = useAppSelector(selectLastUserChatMessage)

  const [triggerInteractWithAiAgentStreaming] = useInteractWithAiAgentStreamingMutation()

  const isNoResult = useMemo(() => isEmailResponseNoResult(emailResponse), [emailResponse])

  const onTextDelta = useCallback(
    (data: EmailResponseResult) => {
      dispatch(setEmailResponse(data))
      const { message } = data
      if (message) {
        dispatch(updateStreamingMessage(message))
      }
    },
    [dispatch],
  )

  const onNewAiAgentId = useCallback(
    (newAiAgentId: string) => {
      dispatch(setEmailResponseAiAgentId(newAiAgentId))
    },
    [dispatch],
  )

  const { onStreamingEvent } = useStreamingChat<EmailResponseResult>({ onTextDelta, onNewAiAgentId })

  const handleResubmit = useCallback(async () => {
    if (!aiAgentId || !lastUserInteraction) {
      return
    }
    dispatch(setEmailResponseInteractionLoading(true))
    try {
      await triggerInteractWithAiAgentStreaming({
        accountId,
        aiAgentId,
        payload: { type: 'chat', query: lastUserInteraction.message },
        onStreamingEvent,
      })
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setEmailResponseInteractionLoading(false))
    }
  }, [accountId, aiAgentId, dispatch, lastUserInteraction, onStreamingEvent, triggerInteractWithAiAgentStreaming])

  const _handleEmailResponseChatInteraction = async (chatMessage: string) => {
    if (!aiAgentId) {
      return
    }
    dispatch(setEmailResponseInteractionLoading(true))
    try {
      dispatch(addMessage({ type: 'user', message: chatMessage }))
      await triggerInteractWithAiAgentStreaming({
        accountId,
        aiAgentId,
        payload: { type: 'chat', query: chatMessage },
        onStreamingEvent,
      })
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setEmailResponseInteractionLoading(false))
    }
  }

  const handleEmailResponseChatInteraction = tracked(
    _handleEmailResponseChatInteraction,
    {
      eventName: TrackEvent.EMAIL_RESPONSE_INTERACTED_WITH,
      eventDetails: { threadId, type: 'chat' },
    },
    {
      eventDetailsExtractor: (chatMessage: string) => ({
        chatMessage,
      }),
    },
  )

  return (
    <StreamingAIAgentChat
      handleSubmit={handleEmailResponseChatInteraction}
      isLoading={isLoading}
      disabled={isEditing || isNoResult || citationIsLoading || !emailResponse}
      collapsible={true}
      className='zocks-bg-color-white'
      handleResubmit={handleResubmit}
    />
  )
}
